import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import api, { fileUrl, API } from "@/lib/api";
import { useTier } from "@/context/TierContext";
import { AppShell } from "@/components/AppShell";
import { StatusBadge, DocTypeBadge, ConditionBadge, DeadlineCountdown, SealedStamp } from "@/components/Badges";
import { ShareLinkManager } from "@/components/ShareLinkManager";
import { DepositProtectionPlan, EvidenceStrengthCard, PremiumAndComparison } from "@/components/ProtectionLayer";
import { DocumentationNotice } from "@/components/DocumentationNotice";
import { InfoTip } from "@/components/InfoTip";
import { TamperEvidentBadge } from "@/components/TamperEvident";
import { CameraFeedback } from "@/components/CameraFeedback";
import { CleaningChecklist } from "@/components/CleaningChecklist";
import { Inventory } from "@/components/Inventory";
import { OutcomePrompt } from "@/components/Outcome";
import { Timeline } from "@/components/Timeline";
import { ProtectionCard } from "@/components/ProtectionCard";
import { DeadlineCountdown as StateDeadlineCard } from "@/components/DepositDeadline";
import { DisputeChargeModule } from "@/components/DisputeChargeModule";
import { SurrenderFlow } from "@/components/SurrenderFlow";
import { WitnessAttestations } from "@/components/WitnessAttestations";
import { MeterReadings } from "@/components/MeterReadings";
import { LeaseScan } from "@/components/LeaseScan";
import { MoveOutRequirements } from "@/components/MoveOutRequirements";
import { ForwardingNotice } from "@/components/ForwardingNotice";
import { CoverageCard } from "@/components/CoverageCard";
import { InspectionRequestTool } from "@/components/InspectionRequestTool";
import { MoveOutPrepPlan } from "@/components/MoveOutPrepPlan";
import { ChallengeProof } from "@/components/ChallengeProof";
import { TenantAttestation } from "@/components/TenantAttestation";
import { ProofReplay } from "@/components/ProofReplay";
import { SelfAuditCard } from "@/components/SelfAuditCard";
import { KeyReturnCapsuleCard } from "@/components/KeyReturnCapsuleCard";
import { ConditionHistoryCard } from "@/components/ConditionHistoryCard";
import { DeductionAuditor } from "@/components/DeductionAuditor";
import { MonthlyCheckup } from "@/components/MonthlyCheckup";
import { MoveOutCountdown } from "@/components/MoveOutCountdown";
import { MoveOutDayCard } from "@/components/MoveOutDayCard";
import { MeterSerialVaultCard } from "@/components/MeterSerialVaultCard";
import { MaintenanceLog } from "@/components/MaintenanceLog";
import { UrgentConditionCapture } from "@/components/UrgentConditionCapture";
import { AccessAmenityRecord } from "@/components/AccessAmenityRecord";
import { PairingPendingPrompt } from "@/components/PairingPendingPrompt";
import { EvidenceTakeout } from "@/components/EvidenceTakeout";
import { Siren } from "lucide-react";
import { TenancySnapshots } from "@/components/TenancySnapshots";
import { KeyDates } from "@/components/KeyDates";
import { CommunicationLog } from "@/components/CommunicationLog";
import { TenancyTimelineCard } from "@/components/TenancyTimelineCard";
import { RentRecord } from "@/components/RentRecord";
import { PropertyVault } from "@/components/PropertyVault";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerFooter, DrawerClose,
} from "@/components/ui/drawer";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetClose,
} from "@/components/ui/sheet";
import { toast } from "sonner";
import {
  ArrowLeft, MapPin, Pencil, Camera, Sparkles, FileText, GitCompareArrows, Loader2,
  DollarSign, Vault, User, Mail, CalendarClock, CheckCircle2, Trash2, DoorOpen, ChevronRight, ChevronDown, AlertTriangle,
  Lock, LockOpen, Download, Send, ShieldCheck, HeartPulse, Share2, Copy, ExternalLink, Scale, Eye, EyeOff, Gavel, ArrowRight, HandCoins, KeyRound,
  Plus, Wrench, MessageSquare, ImagePlus, Home, CalendarRange, DoorClosed, Archive, ArchiveRestore,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";

export default function PropertyDetail() {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const [prop, setProp] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [reports, setReports] = useState([]);
  const [genReport, setGenReport] = useState(false);
  const [health, setHealth] = useState(null);
  const [locking, setLocking] = useState(false);
  const [comparisons, setComparisons] = useState([]);
  const [deadlineStatus, setDeadlineStatus] = useState(null);
  const [markingReturned, setMarkingReturned] = useState(false);
  const [shareInfo, setShareInfo] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [celebrate, setCelebrate] = useState(false);
  const { openUpgrade, isPremium, trialing } = useTier();
  const upsellPendingRef = useRef(false);
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);
  const [urgentOpen, setUrgentOpen] = useState(false);
  const [maintReloadKey, setMaintReloadKey] = useState(0);
  const [archiving, setArchiving] = useState(false);
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [lockConfirmOpen, setLockConfirmOpen] = useState(false);
  const [surrenderOpen, setSurrenderOpen] = useState(false);
  const [leaseSyncTick, setLeaseSyncTick] = useState(0);
  const [proofTick, setProofTick] = useState(0);
  const [shareLinks, setShareLinks] = useState([]);
  const [letters, setLetters] = useState([]);
  const [quickOpen, setQuickOpen] = useState(false);
  const [maintFollowup, setMaintFollowup] = useState(0);
  const [maintCount, setMaintCount] = useState(0);
  const onFollowupCount = useCallback((n) => setMaintFollowup(n), []);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = useCallback(async () => {
    const [{ data: p }, { data: ph }, { data: rp }, { data: h }, { data: cmp }, { data: sl }, { data: lt }, { data: mi }] = await Promise.all([
      api.get(`/properties/${propertyId}`),
      api.get(`/properties/${propertyId}/photos`),
      api.get(`/properties/${propertyId}/reports`),
      api.get(`/properties/${propertyId}/health`),
      api.get(`/properties/${propertyId}/comparisons`).catch(() => ({ data: [] })),
      api.get(`/share-links/${propertyId}`).catch(() => ({ data: [] })),
      api.get(`/dispute-letters/${propertyId}`).catch(() => ({ data: [] })),
      api.get(`/properties/${propertyId}/maintenance`).catch(() => ({ data: [] })),
    ]);
    setProp(p);
    setPhotos(ph);
    setReports(rp);
    setHealth(h);
    setComparisons(cmp);
    setShareLinks(Array.isArray(sl) ? sl : (sl?.links || []));
    setLetters(Array.isArray(lt) ? lt : (lt?.letters || []));
    setMaintFollowup((Array.isArray(mi) ? mi : []).filter((i) => i.needs_followup).length);
    setMaintCount((Array.isArray(mi) ? mi : []).length);
    setDeadlineStatus(p.deadline_status || null);
    setForm({
      move_in_date: p.move_in_date || "",
      move_out_date: p.move_out_date || "",
      deposit_amount: p.deposit_amount ?? "",
      landlord_name: p.landlord_name || "",
      landlord_email: p.landlord_email || "",
      deadline_date: p.deadline_date || "",
    });
    setLoading(false);
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  // Refresh deadline status hourly while the tab is open (date changes once a day).
  useEffect(() => {
    const id = setInterval(() => {
      api.get(`/properties/${propertyId}/deadline-status`).then(({ data }) => setDeadlineStatus(data)).catch(() => {});
    }, 3600000);
    return () => clearInterval(id);
  }, [propertyId]);

  // Deep-link: /property/:id?archive=1 opens the archive confirmation (from the choice dialog).
  useEffect(() => {
    if (prop && prop.archive_status !== "archived" && searchParams.get("archive") === "1") {
      setArchiveConfirmOpen(true);
    }
  }, [prop, searchParams]);


  const markDepositReturned = async (dateStr) => {
    setMarkingReturned(true);
    try {
      const { data } = await api.put(`/properties/${propertyId}/deposit-status`, {
        deposit_returned: true, deposit_returned_date: dateStr,
      });
      setDeadlineStatus(data);
      setProp((pr) => ({ ...pr, deposit_returned: true, deposit_returned_date: dateStr }));
      toast.success("Marked as returned");
    } catch (e) { console.error("mark deposit returned failed", e); toast.error("Could not update"); }
    finally { setMarkingReturned(false); }
  };

  const toggleLock = async () => {
    setLocking(true);
    const wasLocked = prop.is_locked;
    try {
      const { data } = await api.post(`/properties/${propertyId}/${prop.is_locked ? "unlock" : "lock"}`);
      await load();
      setLockConfirmOpen(false);
      if (!wasLocked) {
        upsellPendingRef.current = data?.show_upsell === "first_lock";
        setCelebrate(true);
      } else {
        toast.success("Documentation unlocked");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally {
      setLocking(false);
    }
  };

  // One-time first-lock premium upsell: shown AFTER the celebration dismisses.
  const openLockUpsell = () => {
    if (!upsellPendingRef.current) return;
    upsellPendingRef.current = false;
    openUpgrade("first_lock", { headline: "Your evidence is locked. Now make it work for you." });
    api.post("/users/nudge-seen", { nudge: "first_lock" }).catch(() => {});
  };

  const doArchive = async () => {
    setArchiving(true);
    try {
      await api.post(`/properties/${propertyId}/archive`);
      setArchiveConfirmOpen(false);
      await load();
      toast.success("Property archived — evidence preserved.");
    } catch { toast.error("Could not archive this property."); }
    finally { setArchiving(false); }
  };

  const doUnarchive = async () => {
    setArchiving(true);
    try {
      await api.post(`/properties/${propertyId}/unarchive`);
      await load();
      toast.success("Property unarchived.");
    } catch (e) {
      if (e?.response?.status !== 402) toast.error("Could not unarchive this property.");
      // 402 -> global interceptor opens the multiple_properties UpgradeModal
    } finally { setArchiving(false); }
  };

  const onLockClick = () => {
    if (!prop.is_locked && flaggedPhotos.length > 0) {
      setLockConfirmOpen(true);
      return;
    }
    toggleLock();
  };

  const downloadPdf = () => {
    const comm = localStorage.getItem(`dd_comm_pdf_${propertyId}`) === "1" ? "&comm=1" : "";
    const rent = localStorage.getItem(`dd_rent_pdf_${propertyId}`) === "1" ? "&rent=1" : "";
    window.open(`${API}/properties/${propertyId}/evidence.pdf?${comm}${rent}`.replace("?&", "?"), "_blank");
  };

  const downloadDossier = () => {
    if (!isPremium && !trialing) {
      openUpgrade("exhibit_dossier", { headline: "Share a reader-first exhibit dossier" });
      return;
    }
    const capsule = localStorage.getItem(`dd_capsule_dossier_${propertyId}`) === "1" ? "&capsule=1" : "";
    window.open(`${API}/properties/${propertyId}/evidence.pdf?format=dossier${capsule}`, "_blank");
  };

  const createShareLink = async () => {
    setSharing(true);
    try {
      const { data } = await api.post(`/share-links`, { property_id: propertyId });
      setShareInfo({ url: data.share_url });
      try {
        await navigator.clipboard.writeText(data.share_url);
        toast.success("Share link copied to clipboard");
      } catch {
        toast.success("Share link ready");
      }
      return data.share_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create link");
    } finally {
      setSharing(false);
    }
  };


  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      payload.deposit_amount = payload.deposit_amount === "" ? null : parseFloat(payload.deposit_amount);
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      await api.patch(`/properties/${propertyId}`, payload);
      toast.success("Details saved");
      setEditOpen(false);
      load();
    } catch (e) {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  const markComplete = async () => {
    await api.patch(`/properties/${propertyId}`, { status: prop.status === "completed" ? "in_progress" : "completed" });
    load();
    toast.success("Status updated");
  };

  const removeProperty = async () => {
    if (prop.is_sample) {
      await api.delete(`/sample-property/${propertyId}`);
      toast.success("Sample removed");
    } else {
      await api.delete(`/properties/${propertyId}`);
      toast.success("Property deleted");
    }
    navigate("/dashboard");
  };

  const [vaultBusy, setVaultBusy] = useState(false);
  const downloadVault = () => {
    setVaultBusy(true);
    // Stream download via hidden iframe (attachment response). The same-origin httpOnly
    // session cookie authenticates the request; on error the iframe loads the JSON message
    // without disrupting the page.
    const url = `${API}/properties/${propertyId}/vault.zip`;
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.src = url;
    document.body.appendChild(iframe);
    setTimeout(() => { try { document.body.removeChild(iframe); } catch { /* noop */ } }, 60000);
    setTimeout(() => setVaultBusy(false), 3000);
  };


  const generate = async () => {
    setGenReport(true);
    try {
      const { data } = await api.post(`/properties/${propertyId}/report`);
      setReports((r) => [data, ...r]);
      toast.success("Report generated");
    } catch (e) {
      toast.error("Report generation failed");
    } finally {
      setGenReport(false);
    }
  };

  if (loading) {
    return <AppShell wide><div className="space-y-4"><Skeleton className="h-32 rounded-2xl" /><Skeleton className="h-64 rounded-2xl" /></div></AppShell>;
  }

  const rooms = prop.rooms || [];
  const analyzed = photos.filter((p) => p.ai_status === "done");
  const damaged = analyzed.filter((p) => p.ai_condition === "damaged").length;
  const totalIssues = analyzed.reduce((s, p) => s + (p.ai_issues?.length || 0), 0);
  const photosByRoom = (rid) => photos.filter((p) => p.room_id === rid);
  const flaggedPhotos = photos.filter((p) => p.ai_status === "done" && p.quality?.verdict === "retake");
  const firstFlaggedRoom = flaggedPhotos[0]?.room_id;
  const reviewFlagged = () => navigate(`/capture/${propertyId}/${firstFlaggedRoom || rooms[0]?.room_id || ""}`);

  const renderRooms = () => (
    <div data-testid="rooms-section">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-xl font-semibold text-navy">Rooms &amp; photos</h2>
        <Button data-testid="add-photos-btn" onClick={() => navigate(`/capture/${propertyId}/${rooms[0]?.room_id}`)} className="rounded-xl bg-brand hover:bg-brand-hover text-white">
          <Camera size={16} className="mr-2" /> Add photos
        </Button>
      </div>
      <div className="mt-4 space-y-4">
        {rooms.map((room) => {
          const rp = photosByRoom(room.room_id);
          return (
            <div key={room.room_id} className="rounded-2xl border border-[#E2E8F0] bg-white p-5" data-testid={`room-block-${room.room_id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <DoorOpen size={18} className="text-brand" />
                  <h3 className="font-heading text-lg font-semibold text-navy">{room.name}</h3>
                  <span className="text-sm text-[#94A3B8]">· {rp.length} photos</span>
                </div>
                <button
                  data-testid={`room-capture-${room.room_id}`}
                  onClick={() => navigate(`/capture/${propertyId}/${room.room_id}`)}
                  className="flex items-center gap-1 text-sm font-medium text-brand hover:underline"
                >
                  Capture <ChevronRight size={15} />
                </button>
              </div>
              {rp.length === 0 ? (
                <p className="mt-3 text-sm text-[#94A3B8]">No photos yet in this room.</p>
              ) : (
                <div className="mt-4 grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-6 gap-2.5">
                  {rp.map((p) => (
                    <div key={p.photo_id} className="relative aspect-square rounded-xl overflow-hidden border border-[#E2E8F0] group" data-testid={`detail-photo-${p.photo_id}`}>
                      <img src={fileUrl(p.storage_path)} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                      {(p.ai_status === "pending" || p.ai_status === "processing") && (
                        <div className="absolute inset-0 bg-black/20 overflow-hidden"><div className="dd-scan-beam" /></div>
                      )}
                      {p.ai_status === "done" && (
                        <div className="absolute bottom-1.5 left-1.5"><ConditionBadge condition={p.ai_condition} /></div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderAiReport = () => (
    <div className="rounded-2xl border border-[#E2E8F0] bg-white p-5 sm:p-6" data-testid="ai-report-section">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <FileText size={20} className="text-brand" />
          <h2 className="font-heading text-xl font-semibold text-navy">AI condition report</h2>
        </div>
        <Button data-testid="generate-report-btn" onClick={generate} disabled={genReport || analyzed.length === 0} className="rounded-xl bg-navy hover:bg-navy-hover text-white">
          {genReport ? <Loader2 className="animate-spin" size={16} /> : <><Sparkles size={16} className="mr-2" /> Generate</>}
        </Button>
      </div>
      {analyzed.length === 0 && <p className="mt-3 text-sm text-[#94A3B8]">Add and analyze photos first to generate a report.</p>}
      {reports.length > 0 && (
        <div className="mt-5 rounded-xl bg-[#FAF7F2] border border-[#E2E8F0] p-5" data-testid="report-content">
          <Markdown text={reports[0].content} />
        </div>
      )}
    </div>
  );

  const isLocked = !!prop.is_locked;
  const protectionKey = `${photos.length}-${reports.length}-${comparisons.length}-${prop.is_locked}-${prop.deposit_amount ?? ""}-${proofTick}`;

  const moveOutStarted = prop.doc_type === "move_out" || !!prop.surrender || comparisons.length > 0 || !!prop.move_out_date || !!(prop.outcome && prop.outcome.status);
  const disputeStage = prop.doc_type === "move_out" && (comparisons.length > 0 || letters.length > 0 || !!(prop.outcome && prop.outcome.status));
  // Four tenant-centred destinations. Legacy ?tab= values are transparently remapped.
  const BUCKETS = ["right-now", "document", "records", "respond"];
  const LEGACY = { movein: "records", during: "document", moveout: "respond" };
  const rawTab = searchParams.get("tab");
  const defaultBucket = "right-now";
  const activeTab = BUCKETS.includes(rawTab) ? rawTab : (LEGACY[rawTab] || defaultBucket);
  const setTab = (t) => { const sp = new URLSearchParams(searchParams); sp.set("tab", t); setSearchParams(sp); };
  const scrollToId = (id) => setTimeout(() => document.querySelector(`[data-testid="${id}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 140);

  const startMoveOut = () => {
    if (prop.doc_type === "move_out") navigate(`/capture/${propertyId}/${rooms[0]?.room_id || ""}`);
    else navigate(`/start?doc=move_out&address=${encodeURIComponent(prop.address || "")}`);
  };

  const archived = prop.archive_status === "archived";
  // "Right now" — actionable next steps built ONLY from stored data. Max 4, and never shown
  // alongside the caught-up state. Passive activity (payments/uploads/seals/timeline) is excluded.
  const rightNowActions = (() => {
    if (archived) return [];
    const a = [];
    if (photos.length === 0) a.push({ key: "start", title: "Start documenting", detail: "Add your first room photos to build your baseline.", label: "Add photos", onClick: () => navigate(`/capture/${propertyId}/${rooms[0]?.room_id || ""}`) });
    if (!isLocked && flaggedPhotos.length > 0) a.push({ key: "flagged", title: `Review ${flaggedPhotos.length} photo${flaggedPhotos.length > 1 ? "s" : ""} that could be stronger`, detail: "A few shots may be dark, blurry or tightly framed — retaking is optional.", label: "Review photos", onClick: () => { setTab("records"); scrollToId("retake-nudge"); } });
    if (photos.length > 0 && !isLocked && prop.doc_type === "move_in") a.push({ key: "lock", title: "Lock your documentation", detail: "Seals your timestamped baseline so it can't be changed later.", label: "Lock", onClick: onLockClick });
    if (isLocked && prop.doc_type === "move_in" && shareLinks.length === 0) a.push({ key: "share", title: "Share your report with your landlord", detail: "Send a secure, revocable link for acknowledgment.", label: "Create link", onClick: () => { setTab("records"); scrollToId("share-anchor"); } });
    if (maintFollowup > 0) a.push({ key: "followup", title: `Follow up on ${maintFollowup} recorded repair${maintFollowup > 1 ? "s" : ""}`, detail: "A reported issue hasn't had a response yet — send a courteous follow-up.", label: "Review issue", onClick: () => { setTab("document"); scrollToId("maintenance-anchor"); } });
    if (moveOutStarted && prop.doc_type === "move_out" && photos.length > 0 && comparisons.length === 0) a.push({ key: "compare", title: "Continue move-out records", detail: "Compare your move-in and move-out photos to spot changes.", label: "Run comparison", onClick: () => navigate(`/compare/${propertyId}`) });
    if (prop.doc_type === "move_out" && comparisons.length > 0 && letters.length === 0) a.push({ key: "review-deductions", title: "Review deductions", detail: "Turn your compared condition into a dispute letter or packet.", label: "Open", onClick: () => setTab("respond") });
    return a.slice(0, 4);
  })();
  const caughtUp = rightNowActions.length === 0;
  const lastDocumented = photos.length ? photos.map((p) => p.created_at).filter(Boolean).sort().slice(-1)[0] : null;

  const quickLog = (target) => {
    setQuickOpen(false);
    if (target === "photo") { navigate(`/capture/${propertyId}/${rooms[0]?.room_id || ""}`); return; }
    const map = { maintenance: "maintenance-anchor", rent: "rent-anchor", communication: "comm-anchor", snapshot: "snapshot-anchor" };
    setTab("document");
    scrollToId(map[target]);
  };

  const renderTabFooter = () => (
    <div className="pt-2">
      <Disclosure testId="journey-disclosure" icon={GitCompareArrows} title="Property journey" description="Your documentation timeline for this rental.">
        <Timeline propertyId={propertyId} />
      </Disclosure>
      <Disclosure testId="premium-disclosure" icon={Sparkles} title="TenantSeal Plus tools" description="Evidence package, dispute letter and camera comparison.">
        <PremiumAndComparison />
      </Disclosure>
    </div>
  );

  // ---- Approved dark application-shell nav (desktop sidebar + dense overview) ----
  const NAV = [
    { tab: "right-now", label: "Right now", icon: Sparkles },
    { tab: "document", label: "Document", icon: Camera },
    { tab: "records", label: "My records", icon: FileText },
    { tab: "respond", label: "Prepare & respond", icon: Scale },
  ];

  const sealedCount = prop.is_locked ? photos.length : 0;

  const GROUPS = [
    { tab: "document", title: "Document", icon: Camera, rows: [
      { icon: Camera, label: "Photos & video", count: photos.length, anchor: null, go: () => navigate(`/capture/${propertyId}/${rooms[0]?.room_id || ""}`) },
      { icon: Siren, label: "Urgent conditions", count: damaged || null, anchor: "urgent-anchor" },
      { icon: Wrench, label: "Maintenance & repairs", count: maintCount || null, anchor: "maintenance-anchor", dot: maintFollowup > 0 },
      { icon: MessageSquare, label: "Communications", anchor: "comm-anchor" },
      { icon: DollarSign, label: "Rent & money trail", anchor: "rent-anchor" },
      { icon: CalendarClock, label: "Meters & readings", anchor: "meter-anchor" },
      { icon: KeyRound, label: "Keys & access", anchor: "access-anchor" },
      { icon: HeartPulse, label: "Checkup & reminders", anchor: "checkup-anchor" },
    ] },
    { tab: "records", title: "My records", icon: FileText, rows: [
      { icon: GitCompareArrows, label: "Tenancy timeline", go: () => navigate(`/tenancy-timeline/${propertyId}`) },
      { icon: GitCompareArrows, label: "Condition history", count: comparisons.length || null, go: () => navigate(`/condition-history/${propertyId}`) },
      { icon: Vault, label: "Documents & lease facts", anchor: "vault-anchor" },
      { icon: ShieldCheck, label: "Documentation coverage", anchor: "coverage-anchor", tip: "documentationCoverage" },
      { icon: CheckCircle2, label: "Attestations", anchor: "attest-anchor" },
      { icon: Lock, label: "Sealed records", count: sealedCount || null, status: prop.is_locked ? "sealed" : null, anchor: "share-anchor" },
      { icon: Download, label: "Evidence takeout", anchor: "takeout-anchor" },
    ] },
    { tab: "respond", title: "Prepare & respond", icon: Scale, rows: [
      { icon: DollarSign, label: "Review deductions", anchor: "deduction-anchor", plus: true },
      { icon: Scale, label: "Charge storyboard", anchor: "deduction-auditor-section", plus: true, tip: "chargeStoryboard" },
      { icon: FileText, label: "Dispute letter", count: letters.length || null, plusExport: true, go: () => navigate(`/properties/${propertyId}/dispute-letter`) },
      { icon: GitCompareArrows, label: "Compare move-in / out", count: comparisons.length || null, go: () => navigate(`/compare/${propertyId}`) },
      { icon: FileText, label: "Dossier & exhibits", anchor: "property-actions", plus: true, tip: "exhibitDossier" },
      { icon: Share2, label: "Share & reports", count: shareLinks.length || null, anchor: "share-anchor" },
      { icon: Eye, label: "Privacy-safe share copies", anchor: "share-anchor" },
      { icon: ShieldCheck, label: "Self-audit", anchor: "selfaudit-anchor", plus: true },
    ] },
  ];

  const goRow = (r, tab) => { if (r.go) return r.go(); if (tab) setTab(tab); if (r.anchor) scrollToId(r.anchor); };

  const renderSidebar = () => (
    <aside className="hidden md:flex md:flex-col md:w-56 lg:w-60 shrink-0 sticky top-20 self-start gap-1" data-testid="property-sidebar">
      <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-2">
        {NAV.map((n) => {
          const active = activeTab === n.tab;
          return (
            <button key={n.tab} data-testid={`side-${n.tab}`} onClick={() => setTab(n.tab)}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${active ? "bg-brand text-white" : "text-[#94A3B8] hover:bg-white/5 hover:text-white"}`}>
              <n.icon size={17} /> {n.label}
              {n.tab === "right-now" && rightNowActions.length > 0 && <span className="ml-auto text-[11px] font-semibold rounded-full bg-white/20 px-1.5">{rightNowActions.length}</span>}
            </button>
          );
        })}
      </div>
      {!isPremium && !trialing && (
        <button data-testid="side-plus" onClick={() => openUpgrade("multiple_properties")}
          className="mt-2 rounded-2xl border p-3 text-left transition-colors hover:opacity-90"
          style={{ borderColor: "rgba(124,58,237,0.4)", backgroundColor: "rgba(124,58,237,0.12)" }}>
          <span className="flex items-center gap-1.5 text-sm font-semibold" style={{ color: "#C4A6FF" }}><Sparkles size={14} /> TenantSeal Plus</span>
          <span className="mt-1 block text-xs text-[#94A3B8]">Advanced preparation & output tools.</span>
        </button>
      )}
    </aside>
  );

  const OverviewGroup = ({ g }) => (
    <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4" data-testid={`overview-${g.tab}`}>
      <div className="flex items-center justify-between mb-2">
        <h3 className="flex items-center gap-2 font-heading text-sm font-bold text-white"><g.icon size={16} className="text-[#6FA2F0]" /> {g.title}</h3>
        <button data-testid={`goto-${g.tab}`} onClick={() => setTab(g.tab)} className="text-xs font-semibold text-[#6FA2F0] hover:text-white">Go to {g.title} →</button>
      </div>
      <div className="divide-y divide-white/5">
        {g.rows.map((r, i) => (
          <div key={i} data-testid={`row-${g.tab}-${i}`}
            className="w-full flex items-center gap-2 py-2 px-1 -mx-1 rounded-lg hover:bg-white/[0.03] transition-colors">
            <button onClick={() => goRow(r, g.tab)} className="flex items-center gap-3 min-w-0 text-left">
              <r.icon size={15} className="text-[#64748B] shrink-0" />
              <span className="min-w-0 truncate text-sm text-[#E2E8F0]">{r.label}{r.dot && <span className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-amber align-middle" />}</span>
            </button>
            {r.tip && <InfoTip topic={r.tip} dark side="top" />}
            <button onClick={() => goRow(r, g.tab)} aria-label={r.label} className="flex items-center gap-2 flex-1 justify-end min-w-0">
              {r.plus && !isPremium && !trialing && <span className="text-[10px] font-bold rounded px-1.5 py-0.5" style={{ color: "#C4A6FF", backgroundColor: "rgba(124,58,237,0.16)" }}>PLUS</span>}
              {r.plusExport && !isPremium && !trialing && <span className="text-[10px] font-bold rounded px-1.5 py-0.5" style={{ color: "#C4A6FF", backgroundColor: "rgba(124,58,237,0.16)" }}>PLUS EXPORT</span>}
              {r.status === "sealed" && <span className="text-[11px] font-semibold text-[#5FD39B]">sealed</span>}
              {typeof r.count === "number" && <span className="text-sm font-semibold text-white tabular-nums">{r.count}</span>}
              <ChevronRight size={14} className="text-[#475569] shrink-0" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );

  const renderOverviewPanels = () => (
    <>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="overview-groups">
        {GROUPS.map((g) => <OverviewGroup key={g.tab} g={g} />)}
        <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4" data-testid="activity-summary">
          <h3 className="flex items-center gap-2 font-heading text-sm font-bold text-white mb-3"><ShieldCheck size={16} className="text-[#5FD39B]" /> Activity summary</h3>
          <div className="space-y-2.5 text-sm">
            <div className="flex items-center justify-between"><span className="text-[#94A3B8]">Photos documented</span><span className="font-semibold text-white tabular-nums">{photos.length}</span></div>
            <div className="flex items-center justify-between"><span className="text-[#94A3B8]">Sealed items</span><span className="font-semibold text-white tabular-nums">{sealedCount}</span></div>
            <div className="flex items-center justify-between"><span className="text-[#94A3B8]">Comparisons</span><span className="font-semibold text-white tabular-nums">{comparisons.length}</span></div>
            <div className="flex items-center justify-between"><span className="text-[#94A3B8]">Last documented</span><span className="font-semibold text-white">{lastDocumented ? new Date(lastDocumented).toLocaleDateString() : "No records yet"}</span></div>
          </div>
          <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.03] p-3">
            <p className="text-xs font-semibold text-white">Need help?</p>
            <div className="mt-2 flex gap-3 text-xs">
              <button onClick={() => navigate("/help")} className="text-[#6FA2F0] hover:text-white font-medium">Help center</button>
              <button onClick={() => navigate("/help")} className="text-[#6FA2F0] hover:text-white font-medium">Contact support</button>
            </div>
          </div>
        </div>
      </div>
    </>
  );



  return (
    <AppShell wide>
      {celebrate && (
        <Celebration
          health={health} photoCount={photos.length}
          createdAt={prop.created_at} lockedAt={prop.locked_at}
          onShare={async () => { await createShareLink(); setCelebrate(false); setTimeout(openLockUpsell, 300); }}
          onView={() => { setCelebrate(false); document.querySelector('[data-testid="health-card"]')?.scrollIntoView({ behavior: "smooth" }); setTimeout(openLockUpsell, 300); }}
        />
      )}
      <button data-testid="detail-back-btn" onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-sm text-[#475569] hover:text-navy mb-4">
        <ArrowLeft size={16} /> All properties
      </button>

      <PairingPendingPrompt propertyId={propertyId} />

      {archived && (
        <div data-testid="archived-banner" className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#E2E8F0] bg-[#F1F5F9] px-4 py-3">
          <div className="flex items-center gap-2 text-sm text-[#475569]">
            <Archive size={16} className="text-[#64748B] shrink-0" />
            <span><span className="font-semibold text-navy">Archived — read-only.</span> Evidence preserved.</span>
          </div>
          <Button data-testid="unarchive-btn" onClick={doUnarchive} disabled={archiving}
            className="rounded-xl bg-navy hover:bg-navy-hover text-white h-9 px-4">
            {archiving ? <Loader2 size={15} className="mr-2 animate-spin" /> : <ArchiveRestore size={15} className="mr-2" />} Unarchive
          </Button>
        </div>
      )}

      {/* Header card */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl border border-[#E2E8F0] bg-white p-5 sm:p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <DocTypeBadge docType={prop.doc_type} stage={prop.documented_stage} testId="detail-doctype" />
              <StatusBadge isLocked={!!prop.is_locked} photoCount={photos.length} testId="detail-status" />
              {prop.is_demo && (
                <span data-testid="demo-badge" className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border bg-[#EEF2F7] text-navy border-[#DDE3EC]">Demo</span>
              )}
              {prop.is_sample && (
                <span data-testid="sample-badge" className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border bg-brand/10 text-brand border-brand/30">Sample</span>
              )}
              {prop.is_locked && <SealedStamp lockedAt={prop.locked_at} />}
              {prop.is_locked && <TamperEvidentBadge manifestHash={prop.manifest_hash} lockedAt={prop.locked_at} />}
            </div>
            <div className="flex items-start gap-2">
              <MapPin size={22} className="text-brand mt-0.5 shrink-0" />
              <h1 className="font-heading text-xl sm:text-2xl font-bold tracking-tight text-navy leading-tight">{prop.address}</h1>
            </div>
            <div className="mt-3">{prop.deadline_date ? <DeadlineCountdown deadline={prop.deadline_date} /> : <span className="text-xs text-[#94A3B8]">No deposit-return date recorded</span>}</div>
            {prop.documented_stage === "mid_lease" && (
              <p data-testid="mid-lease-label" className="mt-2 text-sm font-medium text-[#3730A3]">
                Late baseline · Condition as of {prop.created_at ? new Date(prop.created_at).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }) : "today"}
              </p>
            )}
          </div>
          {!archived && (
            <Button data-testid="edit-details-btn" variant="outline" onClick={() => setEditOpen(true)} className="rounded-xl border-[#E2E8F0] shrink-0">
              <Pencil size={16} className="sm:mr-2" /> <span className="hidden sm:inline">Edit</span>
            </Button>
          )}
        </div>

        {/* Stat chips */}
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Stat label="Deposit" value={prop.deposit_amount != null ? `$${prop.deposit_amount.toLocaleString()}` : "None recorded"} icon={Vault} />
          <Stat label="Photos" value={photos.length} icon={Camera} />
          <Stat label="AI review flags" value={damaged} icon={AlertTriangle} warn={damaged > 0} tip="aiReviewFlags" />
          <Stat label="Flagged issues" value={totalIssues} icon={Sparkles} warn={totalIssues > 0} />
        </div>

        {/* Actions */}
        <div className="mt-5">
          <Disclosure testId="property-actions" icon={Download} title="Property actions & exports" description="Lock, reports, dossier, vault, archive and more.">
            <div className="flex flex-wrap gap-2 pt-1">
          {prop.doc_type === "move_in" && !archived && (
            <span className="inline-flex items-center gap-1">
              <Button data-testid="lock-btn" variant="outline" onClick={onLockClick} disabled={locking}
                className={`rounded-xl ${prop.is_locked ? "border-amber-border text-amber-ink bg-amber-soft" : "border-[#E2E8F0]"}`}>
                {locking ? <Loader2 size={16} className="mr-2 animate-spin" /> : prop.is_locked ? <Lock size={16} className="mr-2" /> : <LockOpen size={16} className="mr-2" />}
                {prop.is_locked ? "Locked" : "Lock documentation"}
              </Button>
              <InfoTip topic="lockDocumentation" />
            </span>
          )}
          <span className="inline-flex items-center gap-1">
            <Button data-testid="download-pdf-btn" variant="outline" onClick={downloadPdf} className="rounded-xl border-[#E2E8F0]">
              <Download size={16} className="mr-2" /> Standard report
            </Button>
            <InfoTip topic="evidencePdf" />
          </span>
          <span className="inline-flex items-center gap-1">
            <Button data-testid="download-dossier-btn" variant="outline" onClick={downloadDossier}
              className="rounded-xl border-[#E2E8F0]">
              {(isPremium || trialing) ? <FileText size={16} className="mr-2" /> : <Lock size={16} className="mr-2" />}
              Exhibit dossier
            </Button>
            <InfoTip topic="exhibitDossier" />
          </span>
          {!archived && (
            <Button data-testid="mark-complete-btn" variant="outline" onClick={markComplete} className="rounded-xl border-[#E2E8F0]">
              <CheckCircle2 size={16} className="mr-2" /> {prop.status === "completed" ? "Reopen" : "Mark complete"}
            </Button>
          )}
          {prop.doc_type === "move_out" && (
            <Button data-testid="compare-btn" variant="outline" onClick={() => navigate(`/compare/${propertyId}`)} className="rounded-xl border-[#E2E8F0]">
              <GitCompareArrows size={16} className="mr-2" /> Compare move-in
            </Button>
          )}
          {prop.doc_type === "move_out" && !archived && (
            <Button data-testid="dispute-letter-btn" variant="outline" onClick={() => navigate(`/properties/${propertyId}/dispute-letter`)}
              className="rounded-xl border-[#E2E8F0]">
              <Scale size={16} className="mr-2" /> Generate dispute letter
            </Button>
          )}
          {prop.doc_type === "move_out" && !prop.surrender && photos.length > 0 && !archived && (
            <Button data-testid="complete-moveout-btn" onClick={() => setSurrenderOpen(true)}
              className="rounded-xl bg-navy hover:bg-navy-hover text-white">
              <KeyRound size={16} className="mr-2" /> Complete move-out
            </Button>
          )}
          {(prop.doc_type === "move_out" || (prop.doc_type === "move_in" && prop.is_locked)) && !archived && (
            <Button data-testid="return-request-btn" variant="outline" onClick={() => navigate(`/properties/${propertyId}/return-request`)}
              className="rounded-xl border-[#E2E8F0]">
              <HandCoins size={16} className="mr-2" /> Request deposit return
            </Button>
          )}
          {!prop.is_sample && (
            <span className="inline-flex items-center gap-1">
              <Popover>
                <PopoverTrigger asChild>
                  <Button data-testid="download-vault-btn" variant="outline" disabled={vaultBusy} className="rounded-xl border-[#E2E8F0]">
                    {vaultBusy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Download size={16} className="mr-2" />} Download Evidence Vault (.zip)
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-72" data-testid="vault-confirm-popover">
                  <p className="text-sm text-[#475569] leading-relaxed">Bundles all original photos, an integrity manifest, and generated PDFs — may take a moment for large properties.</p>
                  <Button data-testid="confirm-vault-btn" onClick={downloadVault} disabled={vaultBusy} className="mt-3 w-full rounded-xl bg-brand hover:bg-brand-hover text-white">
                    {vaultBusy ? <><Loader2 size={15} className="mr-2 animate-spin" /> Preparing…</> : "Download ZIP"}
                  </Button>
                </PopoverContent>
              </Popover>
              <InfoTip topic="evidenceVault" />
            </span>
          )}
          {prop.doc_type === "move_in" && prop.is_locked && !archived && (
            <Button data-testid="move-in-notice-btn" variant="outline" onClick={() => setNoticeOpen(true)} className="rounded-xl border-[#E2E8F0]">
              <Send size={16} className="mr-2" /> Generate Move-In Notice
            </Button>
          )}
          {!prop.is_sample && !archived && (
            <Button data-testid="archive-property-btn" variant="outline" onClick={() => setArchiveConfirmOpen(true)}
              className="rounded-xl border-[#E2E8F0]">
              <Archive size={16} className="mr-2" /> Archive property
            </Button>
          )}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button data-testid="delete-property-btn" variant="outline" className="rounded-xl border-[#FECACA] text-[#B91C1C] hover:bg-[#FEE2E2]">
                <Trash2 size={16} className="mr-2" /> {prop.is_sample ? "Remove sample" : "Delete"}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{prop.is_sample ? "Remove this sample?" : "Delete this property?"}</AlertDialogTitle>
                <AlertDialogDescription>{prop.is_sample ? "This removes the sample apartment and its demo records from your account. You can load a fresh sample anytime." : "This removes the property and all its rooms and photos. This cannot be undone."}</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction data-testid="delete-confirm" onClick={removeProperty} className="bg-[#B91C1C] hover:bg-[#DC2626]">{prop.is_sample ? "Remove sample" : "Delete"}</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
            </div>
          </Disclosure>
        </div>

      </motion.div>

      {/* ===== TENANT-CENTRED DESTINATIONS ===== */}
      {prop.doc_type === "move_in" && prop.is_locked && !archived && (
        <div className="mt-6"><DocumentationNotice propertyId={propertyId} open={noticeOpen} onOpenChange={setNoticeOpen} /></div>
      )}

      <div className="mt-6 md:flex md:gap-6 md:items-start">
        {renderSidebar()}
        <div className="flex-1 min-w-0">
      <Tabs value={activeTab} onValueChange={setTab} data-testid="lifecycle-tabs">
        <TabsList className="hidden" data-testid="bucket-tabs">
          <TabsTrigger value="right-now" data-testid="tab-right-now" className="rounded-lg py-2.5 text-xs sm:text-sm data-[state=active]:bg-white data-[state=active]:text-navy">
            <Sparkles size={15} className="sm:mr-1.5" /> <span className="hidden sm:inline">Right now</span>
            {rightNowActions.length > 0 && <span data-testid="right-now-dot" className="ml-1.5 h-2 w-2 rounded-full bg-amber inline-block" />}
          </TabsTrigger>
          <TabsTrigger value="document" data-testid="tab-document" className="rounded-lg py-2.5 text-xs sm:text-sm data-[state=active]:bg-white data-[state=active]:text-navy">
            <Camera size={15} className="sm:mr-1.5" /> <span className="hidden sm:inline">Document</span>
            {maintFollowup > 0 && <span data-testid="during-followup-dot" className="ml-1.5 h-2 w-2 rounded-full bg-amber inline-block" />}
          </TabsTrigger>
          <TabsTrigger value="records" data-testid="tab-records" className="rounded-lg py-2.5 text-xs sm:text-sm data-[state=active]:bg-white data-[state=active]:text-navy">
            <FileText size={15} className="sm:mr-1.5" /> <span className="hidden sm:inline">My records</span>
          </TabsTrigger>
          <TabsTrigger value="respond" data-testid="tab-respond" className="rounded-lg py-2.5 text-xs sm:text-sm data-[state=active]:bg-white data-[state=active]:text-navy">
            <Scale size={15} className="sm:mr-1.5" /> <span className="hidden sm:inline">Prepare & respond</span>
          </TabsTrigger>
        </TabsList>

        {/* -------- RIGHT NOW -------- */}
        <TabsContent value="right-now" className="mt-5 space-y-4 focus-visible:outline-none" data-testid="tabpanel-right-now">
          {!caughtUp ? (
            <div data-testid="right-now-actions" className="space-y-3">
              {rightNowActions.map((a) => (
                <div key={a.key} data-testid={`rightnow-${a.key}`} className="rounded-2xl border-2 border-brand/25 bg-gradient-to-br from-brand/[0.05] to-white p-5 flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <h3 className="font-heading text-lg font-bold text-navy">{a.title}</h3>
                    <p className="text-sm text-[#475569] mt-0.5">{a.detail}</p>
                  </div>
                  {!archived && a.label && (
                    <Button data-testid={`rightnow-cta-${a.key}`} onClick={a.onClick} disabled={sharing || locking}
                      className="rounded-xl bg-brand hover:bg-brand-hover text-white shrink-0">
                      {a.label} <ArrowRight size={15} className="ml-1.5" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div data-testid="right-now-caught-up" className="rounded-2xl border border-[#E2E8F0] bg-white p-6">
              <p className="font-heading text-xl font-bold text-navy">You’re all caught up.</p>
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                <div className="rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] p-3"><div className="text-[#94A3B8] text-xs">Photos documented</div><div className="font-semibold text-navy">{photos.length}</div></div>
                {prop.is_locked && <div className="rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] p-3"><div className="text-[#94A3B8] text-xs">Sealed</div><div className="font-semibold text-navy">{prop.locked_at ? new Date(prop.locked_at).toLocaleDateString() : "yes"} · verify anytime</div></div>}
                {lastDocumented && <div className="rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] p-3"><div className="text-[#94A3B8] text-xs">Last documented</div><div className="font-semibold text-navy">{new Date(lastDocumented).toLocaleDateString()}</div></div>}
              </div>
              {deadlineStatus?.configured && deadlineStatus?.deadline_date && (
                <p data-testid="rightnow-deadline-factual" className="mt-3 text-sm text-[#475569]">
                  Deposit-return date you recorded: {new Date(deadlineStatus.deadline_date).toLocaleDateString()}{typeof deadlineStatus.days_remaining === "number" && deadlineStatus.days_remaining < 0 ? " · passed" : ""}
                  <span className="block text-xs text-[#94A3B8]">This is the date you entered. TenantSeal doesn’t determine legal deadlines.</span>
                </p>
              )}
            </div>
          )}
          {/* Mobile quick-nav tiles (large touch targets) */}
          <div className="grid grid-cols-2 gap-3 md:hidden" data-testid="mobile-tiles">
            {[{ t: "document", label: "Document", icon: Camera }, { t: "records", label: "My records", icon: FileText }, { t: "respond", label: "Prepare & respond", icon: Scale }].map((x) => (
              <button key={x.t} data-testid={`tile-${x.t}`} onClick={() => setTab(x.t)}
                className="flex flex-col items-start gap-2 rounded-2xl border border-white/10 bg-white/[0.04] p-4 min-h-[84px] active:scale-[0.98] transition-transform">
                <x.icon size={20} className="text-[#6FA2F0]" />
                <span className="text-sm font-semibold text-white text-left">{x.label}</span>
              </button>
            ))}
            <button data-testid="tile-more" onClick={() => navigate("/help")}
              className="flex flex-col items-start gap-2 rounded-2xl border border-white/10 bg-white/[0.04] p-4 min-h-[84px] active:scale-[0.98] transition-transform">
              <HeartPulse size={20} className="text-[#6FA2F0]" />
              <span className="text-sm font-semibold text-white text-left">More & help</span>
            </button>
          </div>

          {renderOverviewPanels()}
        </TabsContent>

        {/* -------- MY RECORDS -------- */}
        <TabsContent value="records" className="mt-5 space-y-6 focus-visible:outline-none" data-testid="tabpanel-movein">
          <DepositProtectionPlan propertyId={propertyId} refreshKey={protectionKey} />

          <div data-testid="coverage-anchor">
            <CoverageCard propertyId={propertyId} />
          </div>

          {prop.doc_type === "move_in" ? (
            <>
              {renderRooms()}

              {!prop.is_locked && flaggedPhotos.length > 0 && (
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                  data-testid="retake-nudge"
                  className="rounded-2xl border border-amber-border bg-amber-soft p-4 flex items-start gap-3">
                  <AlertTriangle size={20} className="text-amber-ink mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-amber-ink">
                      {flaggedPhotos.length} photo{flaggedPhotos.length > 1 ? "s" : ""} could be stronger
                    </p>
                    <p className="text-sm text-amber-ink/80 mt-0.5">
                      A few shots may be too dark, blurry or tightly framed. Retaking them now makes your evidence clearer — it's optional.
                    </p>
                  </div>
                  <Button data-testid="review-flagged-btn" variant="outline" onClick={reviewFlagged}
                    className="rounded-xl border-amber-border text-amber-ink bg-white/60 hover:bg-white shrink-0">
                    Review photos
                  </Button>
                </motion.div>
              )}

              {renderAiReport()}
              <EvidenceStrengthCard propertyId={propertyId} refreshKey={protectionKey} />
              <ChallengeProof propertyId={propertyId} isLocked={!!prop.is_locked} onChanged={() => setProofTick((t) => t + 1)} />
              <TenantAttestation propertyId={propertyId} isLocked={!!prop.is_locked} manifestHash={prop.manifest_hash} onChanged={() => setProofTick((t) => t + 1)} />
              <MeterReadings propertyId={propertyId} phase={prop.doc_type} readOnly={!!prop.surrender} />

              {photos.length > 0 && (
                <Inventory
                  propertyId={propertyId}
                  photosById={Object.fromEntries(photos.map((p) => [p.photo_id, p]))}
                  rooms={rooms}
                />
              )}

              {prop.is_locked && (
                <ProtectionCard photoCount={photos.length} lockedAt={prop.locked_at} />
              )}
              {prop.is_locked && health && (
                <HealthCard health={health} navigate={navigate} propertyId={propertyId} />
              )}
            </>
          ) : (
            <p className="text-sm text-[#94A3B8]" data-testid="movein-empty">Move-in photos are documented on your move-in record for this rental.</p>
          )}

          {/* Share with landlord — secure, revocable links with acknowledgment receipts */}
          <div data-testid="share-anchor">
            <ShareLinkManager propertyId={propertyId} isSample={prop.is_sample} />
          </div>

          {/* Witness attestations */}
          <WitnessAttestations propertyId={propertyId} property={prop} />

          {/* Meter & Serial Vault — free utility (Feature 24) */}
          <MeterSerialVaultCard propertyId={propertyId} />

          {/* "Why not just use your camera?" value feedback */}
          {prop.is_locked && prop.doc_type === "move_in"
            ? <CameraFeedback propertyId={propertyId} context="lock" />
            : <CameraFeedback propertyId={propertyId} context="report_view" />}

          {renderTabFooter()}
        </TabsContent>

        {/* -------- DOCUMENT -------- */}
        <TabsContent value="document" className="mt-5 space-y-6 focus-visible:outline-none" data-testid="tabpanel-during">
          {/* Urgent condition capture — free guided documentation (Feature 29) */}
          {!archived && !prop.is_locked && !prop.surrender && (
            <div className="rounded-2xl border border-amber-400/30 bg-amber-400/5 p-4 flex items-start gap-3" data-testid="urgent-capture-card">
              <div className="grid place-items-center h-10 w-10 rounded-xl bg-amber-400/15 shrink-0">
                <Siren size={20} className="text-amber" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-navy">Something urgent right now?</p>
                <p className="text-sm text-[#475569] mt-0.5">Guided shots for leaks, pests, appliances and more — documented in seconds.</p>
                <Button size="sm" className="mt-3 rounded-full bg-navy hover:bg-navy/90 text-white" data-testid="urgent-capture-open-btn" onClick={() => setUrgentOpen(true)}>
                  <Siren size={15} className="mr-1.5" /> Urgent capture
                </Button>
              </div>
            </div>
          )}
          {/* Tenancy Timeline — free chronological overview across the whole tenancy */}
          <TenancyTimelineCard propertyId={propertyId} />
          <AccessAmenityRecord propertyId={propertyId} archived={archived} />
          <MonthlyCheckup propertyId={propertyId} readOnly={!!prop.is_locked} />
          <div data-testid="maintenance-anchor">
            <MaintenanceLog key={maintReloadKey} propertyId={propertyId} readOnly={!!prop.is_locked} onFollowupCount={onFollowupCount} />
          </div>
          <div data-testid="snapshot-anchor">
            <TenancySnapshots propertyId={propertyId} />
          </div>
          <div data-testid="comm-anchor">
            <CommunicationLog propertyId={propertyId} />
          </div>
          <div data-testid="rent-anchor">
            <RentRecord propertyId={propertyId} />
          </div>
          <KeyDates propertyId={propertyId} />
          {!prop.is_sample && <PropertyVault propertyId={propertyId} />}
          <LeaseScan
            propertyId={propertyId}
            leaseSummary={prop.lease_summary}
            depositAmount={prop.deposit_amount}
            onSummary={(s) => { setProp((pr) => ({ ...pr, lease_summary: s })); setLeaseSyncTick((t) => t + 1); }}
            onPrefillDeposit={async (amt) => {
              try {
                await api.patch(`/properties/${propertyId}`, { deposit_amount: amt });
                setProp((pr) => ({ ...pr, deposit_amount: amt }));
                setForm((f) => ({ ...f, deposit_amount: amt }));
                toast.success("Deposit amount added to this property");
              } catch (e) {
                console.error("prefill deposit failed", e);
                toast.error("Couldn't set the deposit amount");
              }
            }}
          />
          {renderTabFooter()}
        </TabsContent>

        {/* -------- PREPARE & RESPOND -------- */}
        <TabsContent value="respond" className="mt-5 space-y-6 focus-visible:outline-none" data-testid="tabpanel-moveout">
          {(() => {
            const capturedMoveOut = prop.doc_type === "move_out" && photos.length > 0;
            const steps = [
              { key: "photos", label: "Take final room photos", done: capturedMoveOut, action: startMoveOut, cta: "Capture" },
              { key: "compare", label: "Run the move-in vs move-out comparison", done: comparisons.length > 0, action: () => navigate(`/compare/${propertyId}`), cta: "Compare" },
              { key: "clean", label: "Complete the cleaning checklist", done: false, action: (prop.is_locked && prop.doc_type === "move_in") ? () => scrollToId("cleaning-anchor") : null },
              { key: "keys", label: "Record your key return", done: !!prop.surrender, action: (prop.doc_type === "move_out" && photos.length > 0) ? () => setSurrenderOpen(true) : null, cta: "Record" },
              { key: "deadline", label: "Enter your deposit-return deadline date", done: !!deadlineStatus?.configured, action: () => scrollToId("deposit-deadline-section"), cta: "Enter" },
              { key: "letter", label: "Generate your return-request letter", done: letters.length > 0, action: () => navigate(`/properties/${propertyId}/return-request`), cta: "Generate" },
              { key: "export", label: "Export your evidence package", done: false, action: downloadPdf, cta: "Export" },
            ];
            return (
              <div className="rounded-2xl border-2 border-brand/30 bg-gradient-to-br from-brand/[0.06] to-white p-5 sm:p-6" data-testid="moveout-checkout">
                <h3 className="font-heading text-xl font-bold text-navy">Move-out checkout</h3>
                <p className="mt-1 text-sm text-[#475569]">Follow these steps to document your move-out and prepare your deposit return.</p>
                <Button data-testid="moveout-start-capture-btn" onClick={startMoveOut}
                  className="mt-3 h-11 px-6 rounded-xl bg-brand hover:bg-brand-hover text-white font-semibold">
                  <Camera size={17} className="mr-2" /> Start move-out capture
                </Button>
                <ol className="mt-4 space-y-2">
                  {steps.map((s, idx) => (
                    <li key={s.key} data-testid={`moveout-step-${s.key}`} className="flex items-center gap-3 rounded-xl border border-[#E2E8F0] bg-white px-3 py-2.5">
                      <span className={`grid place-items-center h-6 w-6 rounded-full text-xs font-bold shrink-0 ${s.done ? "bg-[#177245] text-white" : "bg-[#F1F5F9] text-[#94A3B8]"}`}>
                        {s.done ? <CheckCircle2 size={14} /> : idx + 1}
                      </span>
                      <span className={`flex-1 min-w-0 text-sm ${s.done ? "text-[#94A3B8] line-through" : "text-navy"}`}>{s.label}</span>
                      {s.action && !s.done && (
                        <button data-testid={`moveout-step-action-${s.key}`} onClick={s.action} className="text-sm font-semibold text-brand hover:underline shrink-0 inline-flex items-center gap-1">
                          {s.cta || "Do this"} <ChevronRight size={14} />
                        </button>
                      )}
                    </li>
                  ))}
                </ol>
              </div>
            );
          })()}

          {prop.doc_type === "move_out" && (
            <>
              <MoveOutDayCard propertyId={propertyId} />
              {renderRooms()}
              {renderAiReport()}
              <EvidenceStrengthCard propertyId={propertyId} refreshKey={protectionKey} />
              <ChallengeProof propertyId={propertyId} isLocked={!!prop.is_locked} onChanged={() => setProofTick((t) => t + 1)} />
              <MeterReadings propertyId={propertyId} phase={prop.doc_type} readOnly={!!prop.surrender} />

              {/* Proof Replay — guided same-view recapture (TenantSeal Plus) */}
              <ProofReplay propertyId={propertyId} archived={archived} />

              {/* Pre-Move-Out Self-Audit — difference review over confirmed Proof Replay pairs (TenantSeal Plus) */}
              <SelfAuditCard propertyId={propertyId} />

              {/* Key Return Capsule — final move-out & return record (FREE) */}
              <KeyReturnCapsuleCard propertyId={propertyId} />

              {/* Condition History — chronological view of existing evidence (TenantSeal Plus) */}
              <ConditionHistoryCard propertyId={propertyId} archived={archived} />


              {/* Move-in vs move-out comparison */}
              <div className="rounded-2xl border border-[#E2E8F0] bg-white p-5 sm:p-6" data-testid="comparison-section">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <GitCompareArrows size={20} className="text-brand" />
                    <h2 className="font-heading text-xl font-semibold text-navy">Move-in vs move-out comparison</h2>
                  </div>
                  <Button data-testid="run-comparison-btn" onClick={() => navigate(`/compare/${propertyId}`)} className="rounded-xl bg-navy hover:bg-navy-hover text-white">
                    <GitCompareArrows size={16} className="mr-2" /> {comparisons.length > 0 ? "View / re-run" : "Run comparison"}
                  </Button>
                </div>
                <p className="mt-2 text-sm text-[#94A3B8]">
                  {comparisons.length > 0 ? `${comparisons.length} comparison${comparisons.length > 1 ? "s" : ""} run — open the slider to review changes.` : "Compare your move-in and move-out photos to spot changes automatically."}
                </p>
              </div>
            </>
          )}

          {/* Prepare for move-out: AI room-by-room cleaning checklist (locked move-in only) */}
          {prop.is_locked && prop.doc_type === "move_in" && (
            <div data-testid="cleaning-anchor">
              <CleaningChecklist
                propertyId={propertyId}
                photosById={Object.fromEntries(photos.map((p) => [p.photo_id, p]))}
                reloadSignal={leaseSyncTick}
              />
            </div>
          )}

          {/* Deduction analysis (move-out) */}
          {prop.doc_type === "move_out" && (
            <div data-testid="deduction-audit-section">
              <DisputeChargeModule propertyId={propertyId} />
            </div>
          )}

          {/* Deposit deduction auditor (Premium) — move-out */}
          {prop.doc_type === "move_out" && (
            <div data-testid="deduction-auditor-section">
              <DeductionAuditor propertyId={propertyId} archived={archived} />
            </div>
          )}

          {/* Move-out countdown plan (Premium) */}
          <MoveOutCountdown propertyId={propertyId} archived={archived} />

          {/* Move-out requirements checklist */}
          <div data-testid="moveout-checklist" className="scroll-mt-24">
            <MoveOutRequirements propertyId={propertyId} reloadSignal={leaseSyncTick} />
          </div>

          {/* Move-out prep plan (Premium AI checklist) */}
          <MoveOutPrepPlan propertyId={propertyId} archived={archived} />

          {/* Pre-move-out inspection request — email scheduling request (free for all tiers) */}
          <InspectionRequestTool propertyId={propertyId} address={prop.address}
            onLogResponse={() => { setTab("document"); scrollToId("comm-anchor"); }} />

          {/* Forwarding address notice (free for all tiers) */}
          <ForwardingNotice propertyId={propertyId} address={prop.address}
            landlordName={prop.landlord_name} moveOutDate={prop.move_out_date} />

          {/* Deposit-return deadline tracker (factual; never auto-recommends Small Claims) */}
          <div data-testid="deposit-deadline-section">
            <StateDeadlineCard
              variant="full"
              status={deadlineStatus}
              onSetup={() => setEditOpen(true)}
              onMarkReturned={markDepositReturned}
              marking={markingReturned}
            />
          </div>

          {/* Deliberate Small Claims preparation entry (never auto-recommended) */}
          <div data-testid="small-claims-entry" className="rounded-2xl border border-[#E2E8F0] bg-white p-4 flex items-center justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0">
              <span className="grid place-items-center h-9 w-9 rounded-xl bg-[#F1F5F9] text-navy shrink-0"><Gavel size={17} /></span>
              <div className="min-w-0">
                <h3 className="font-heading text-sm font-semibold text-navy flex items-center gap-1.5">Small claims preparation <InfoTip topic="smallClaimsPacket" /></h3>
                <p className="text-xs text-[#64748B]">Organize your documented evidence into a packet if you decide to pursue it. TenantSeal doesn’t give legal advice.</p>
              </div>
            </div>
            <Button data-testid="open-small-claims-btn" variant="outline" size="sm" className="rounded-lg shrink-0"
              onClick={() => navigate(`/properties/${propertyId}/small-claims`)}>Open</Button>
          </div>

          {prop.surrender && (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              data-testid="surrender-receipt-card"
              className="rounded-2xl border border-navy/15 bg-navy px-5 py-4 text-white">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-start gap-3">
                  <span className="grid place-items-center h-10 w-10 rounded-xl bg-white/10 text-amber shrink-0"><KeyRound size={20} /></span>
                  <div>
                    <p className="font-heading font-semibold flex items-center gap-1.5"><CheckCircle2 size={16} className="text-amber" /> Move-out complete</p>
                    <p className="text-sm text-white/70">Keys surrendered {new Date(prop.surrender.surrendered_at).toLocaleDateString()} · receipt ready to send.</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button data-testid="receipt-copy-btn" variant="outline"
                    onClick={async () => {
                      const url = `${window.location.origin}/surrender/${prop.surrender.receipt_token}`;
                      try {
                        if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(url); else throw new Error();
                        toast.success("Receipt link copied");
                      } catch {
                        const ta = document.createElement("textarea"); ta.value = url; ta.style.position = "fixed"; ta.style.opacity = "0";
                        document.body.appendChild(ta); ta.focus(); ta.select();
                        let ok = false; try { ok = document.execCommand("copy"); } catch { ok = false; }
                        document.body.removeChild(ta);
                        ok ? toast.success("Receipt link copied") : toast.error("Couldn't copy — please try again.");
                      }
                    }}
                    className="rounded-xl border-white/25 bg-white/5 text-white hover:bg-white/15">
                    <Copy size={15} className="mr-2" /> Copy link
                  </Button>
                  <a data-testid="receipt-email-btn"
                    href={`mailto:?subject=${encodeURIComponent("Keys surrendered — move-out documented")}&body=${encodeURIComponent(`Hello,\n\nI've returned the keys and documented the move-out. View the surrender receipt here:\n${window.location.origin}/surrender/${prop.surrender.receipt_token}\n\nThank you.`)}`}>
                    <Button variant="outline" className="rounded-xl border-white/25 bg-white/5 text-white hover:bg-white/15"><Mail size={15} className="mr-2" /> Email</Button>
                  </a>
                  <a data-testid="receipt-pdf-btn" href={`${API}/surrender/${prop.surrender.receipt_token}/receipt.pdf`} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" className="rounded-xl border-white/25 bg-white/5 text-white hover:bg-white/15"><Download size={15} className="mr-2" /> PDF</Button>
                  </a>
                </div>
              </div>
            </motion.div>
          )}

          {/* Record your outcome (move-out) */}
          <div data-testid="outcome-anchor">
            {prop.doc_type === "move_out" && (
              <OutcomePrompt property={prop} onUpdated={(oc) => setProp((c) => ({ ...c, outcome: oc }))} />
            )}
          </div>

          {renderTabFooter()}
        </TabsContent>
      </Tabs>
        </div>
      </div>

      <div className="mt-6">
        <EvidenceTakeout scope="property" propertyId={propertyId} />
      </div>

      <AlertDialog open={archiveConfirmOpen} onOpenChange={setArchiveConfirmOpen}>
        <AlertDialogContent data-testid="archive-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Archive this property?</AlertDialogTitle>
            <AlertDialogDescription>
              Your evidence stays locked, viewable, and exportable — nothing is deleted. Archiving frees your active slot for a new address. You can unarchive later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {!isPremium && (
            <p className="text-xs text-[#64748B]" data-testid="archive-upgrade-hint">
              Want both active at once? TenantSeal Plus includes unlimited properties.{" "}
              <button type="button" onClick={() => { setArchiveConfirmOpen(false); openUpgrade("multiple_properties"); }}
                className="font-semibold text-brand underline" data-testid="archive-upgrade-link">Upgrade</button>
            </p>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="archive-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="archive-confirm" onClick={doArchive} disabled={archiving} className="bg-navy hover:bg-navy-hover">
              {archiving ? "Archiving…" : "Archive"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* ===== QUICK-LOG FAB ===== */}
      {!archived && (
      <button
        data-testid="quick-log-fab"
        onClick={() => setQuickOpen(true)}
        aria-label="Quick log"
        className="fixed right-5 z-50 grid place-items-center h-14 w-14 rounded-full bg-brand text-white shadow-xl shadow-brand/30 hover:bg-brand-hover active:scale-95 transition-transform bottom-[calc(5.25rem+env(safe-area-inset-bottom))] md:bottom-[calc(1.5rem+env(safe-area-inset-bottom))]"
      >
        <Plus size={26} />
      </button>
      )}
      <Sheet open={quickOpen} onOpenChange={setQuickOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl" data-testid="quick-log-sheet">
          <SheetHeader>
            <SheetTitle>What do you want to log?</SheetTitle>
          </SheetHeader>
          <div className="mt-4 grid gap-2" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
            {[
              { id: "photo", icon: Camera, label: "Photo", desc: "Capture a new room photo" },
              { id: "maintenance", icon: Wrench, label: "Maintenance issue", desc: "Log a repair or problem" },
              { id: "rent", icon: DollarSign, label: "Rent payment", desc: "Record a rent payment" },
              { id: "communication", icon: MessageSquare, label: "Communication", desc: "Note a landlord interaction" },
              { id: "snapshot", icon: ImagePlus, label: "Snapshot", desc: "Add a condition snapshot" },
            ].map((o) => (
              <button
                key={o.id}
                data-testid={`quick-log-${o.id}`}
                onClick={() => quickLog(o.id)}
                className="min-h-[56px] w-full flex items-center gap-3 rounded-xl border border-[#E2E8F0] px-4 py-3 text-left transition-colors hover:border-brand hover:bg-brand/5"
              >
                <span className="grid place-items-center h-10 w-10 rounded-xl bg-brand/10 text-brand shrink-0"><o.icon size={18} /></span>
                <span className="min-w-0">
                  <span className="block font-semibold text-navy">{o.label}</span>
                  <span className="block text-xs text-[#94A3B8]">{o.desc}</span>
                </span>
              </button>
            ))}
            <SheetClose asChild>
              <Button variant="outline" className="mt-1 h-11 rounded-xl" data-testid="quick-log-cancel">Cancel</Button>
            </SheetClose>
          </div>
        </SheetContent>
      </Sheet>

      {/* ===== Modals / overlays ===== */}
      <SurrenderFlow propertyId={propertyId} address={prop.address}
        open={surrenderOpen} onOpenChange={setSurrenderOpen} onDone={load} />

      <UrgentConditionCapture property={prop} rooms={rooms} open={urgentOpen} onOpenChange={setUrgentOpen}
        onLinked={() => setMaintReloadKey((k) => k + 1)} />

      {/* Move-in documentation notice is rendered near the top of the page. */}

      <AlertDialog open={lockConfirmOpen} onOpenChange={setLockConfirmOpen}>
        <AlertDialogContent data-testid="lock-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Lock with {flaggedPhotos.length} flagged photo{flaggedPhotos.length > 1 ? "s" : ""}?</AlertDialogTitle>
            <AlertDialogDescription>
              {flaggedPhotos.length} photo{flaggedPhotos.length > 1 ? "s have" : " has"} a retake suggestion (too dark, blurry, or framing).
              Locking seals your documentation as-is. You can review them first, or lock anyway.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="lock-review-btn" onClick={reviewFlagged}>Review photos</AlertDialogCancel>
            <AlertDialogAction data-testid="lock-anyway-btn" onClick={toggleLock} className="bg-navy hover:bg-navy-hover">
              Lock anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


      {/* Edit drawer */}
      <Drawer open={editOpen} onOpenChange={setEditOpen}>
        <DrawerContent>
          <div className="mx-auto w-full max-w-lg">
            <DrawerHeader>
              <DrawerTitle className="font-heading text-xl">Property details</DrawerTitle>
            </DrawerHeader>
            <div className="px-4 space-y-4 max-h-[55vh] overflow-y-auto">
              <Field label="Deposit amount ($)" icon={DollarSign}>
                <Input data-testid="edit-deposit" type="number" value={form.deposit_amount} onChange={(e) => setForm({ ...form, deposit_amount: e.target.value })} placeholder="1500" className="h-12 rounded-xl" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Move-in date" icon={CalendarClock}>
                  <Input data-testid="edit-movein" type="date" value={form.move_in_date} onChange={(e) => setForm({ ...form, move_in_date: e.target.value })} className="h-12 rounded-xl" />
                </Field>
                <Field label="Move-out date" icon={CalendarClock}>
                  <Input data-testid="edit-moveout" type="date" value={form.move_out_date} onChange={(e) => setForm({ ...form, move_out_date: e.target.value })} className="h-12 rounded-xl" />
                </Field>
              </div>
              <Field label="Deposit return deadline" icon={CalendarClock}>
                <Input data-testid="edit-deadline" type="date" value={form.deadline_date} onChange={(e) => setForm({ ...form, deadline_date: e.target.value })} className="h-12 rounded-xl" />
                <p className="mt-1.5 text-xs text-[#94A3B8]">Deposit return deadlines vary by state/country — check your lease or your state's official housing resources, then enter the date here.</p>
              </Field>
              <Field label="Landlord name" icon={User}>
                <Input data-testid="edit-landlord-name" value={form.landlord_name} onChange={(e) => setForm({ ...form, landlord_name: e.target.value })} placeholder="Jane Property Co." className="h-12 rounded-xl" />
              </Field>
              <Field label="Landlord email" icon={Mail}>
                <Input data-testid="edit-landlord-email" type="email" value={form.landlord_email} onChange={(e) => setForm({ ...form, landlord_email: e.target.value })} placeholder="landlord@email.com" className="h-12 rounded-xl" />
              </Field>
            </div>
            <DrawerFooter>
              <Button data-testid="save-details-btn" onClick={save} disabled={saving} className="h-12 rounded-xl bg-brand hover:bg-brand-hover text-white">
                {saving ? <Loader2 className="animate-spin" size={18} /> : "Save details"}
              </Button>
              <DrawerClose asChild><Button variant="ghost">Cancel</Button></DrawerClose>
            </DrawerFooter>
          </div>
        </DrawerContent>
      </Drawer>
    </AppShell>
  );
}

function Disclosure({ title, description, icon: Icon, defaultOpen = false, testId, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="mt-4 rounded-2xl border border-[#E2E8F0] bg-white overflow-hidden" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        data-testid={testId ? `${testId}-toggle` : undefined}
        className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left transition-colors hover:bg-[#FAFBFC]"
      >
        <span className="flex items-center gap-2.5 min-w-0">
          {Icon && <Icon size={18} className="text-brand shrink-0" />}
          <span className="min-w-0">
            <span className="block font-heading font-semibold text-navy">{title}</span>
            {description && <span className="block text-xs text-[#94A3B8] truncate">{description}</span>}
          </span>
        </span>
        <ChevronDown size={18} className={`text-[#94A3B8] shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-4 pb-4 pt-1 border-t border-[#F1F5F9]" data-testid={testId ? `${testId}-body` : undefined}>{children}</div>}
    </div>
  );
}


function Stat({ label, value, icon: Icon, warn, tip }) {
  return (
    <div className={`rounded-xl border p-3 ${warn ? "border-[#FDE68A] bg-[#FEF3C7]/40" : "border-[#E2E8F0] bg-[#FAF7F2]"}`}>
      <div className="flex items-center gap-1.5 text-[#94A3B8]"><Icon size={14} /><span className="text-[11px] font-semibold uppercase tracking-wider">{label}</span>{tip && <InfoTip topic={tip} />}</div>
      <p className={`mt-1 font-heading text-xl font-bold ${warn ? "text-[#92400E]" : "text-navy"}`}>{value}</p>
    </div>
  );
}

function Field({ label, icon: Icon, children }) {
  return (
    <div>
      <Label className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-[#94A3B8] mb-1.5"><Icon size={13} /> {label}</Label>
      {children}
    </div>
  );
}

function Markdown({ text }) {
  const lines = text.split("\n");
  return (
    <div className="prose-dd space-y-2 text-[#334155]">
      {lines.map((ln, i) => {
        const k = `${i}-${ln.slice(0, 24)}`;
        if (ln.startsWith("## ")) return <h3 key={k} className="font-heading text-lg font-semibold text-navy mt-4 first:mt-0">{ln.slice(3)}</h3>;
        if (ln.startsWith("# ")) return <h2 key={k} className="font-heading text-xl font-bold text-navy">{ln.slice(2)}</h2>;
        if (ln.startsWith("- ")) return <li key={k} className="ml-5 list-disc text-sm leading-relaxed">{ln.slice(2)}</li>;
        if (ln.trim() === "") return null;
        return <p key={k} className="text-sm leading-relaxed">{ln}</p>;
      })}
    </div>
  );
}


const SCORE_STYLE = {
  "Excellent": "bg-[#D1FAE5] text-[#177245] border-[#A7F3D0]",
  "Minor issues": "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]",
  "Serious issues": "bg-[#FEE2E2] text-[#B91C1C] border-[#FECACA]",
  "Pending": "bg-[#F1F5F9] text-[#475569] border-[#E2E8F0]",
};

function ScorePill({ score, testId }) {
  return (
    <span data-testid={testId} className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${SCORE_STYLE[score] || SCORE_STYLE.Pending}`}>
      {score}
    </span>
  );
}

function HealthCard({ health, navigate, propertyId }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className="mt-6 rounded-2xl border border-[#E2E8F0] bg-white p-5 sm:p-6" data-testid="health-card"
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <HeartPulse size={20} className="text-brand" />
          <h2 className="font-heading text-xl font-semibold text-navy">Property Health Score</h2>
        </div>
        <ScorePill score={health.overall_score} testId="overall-health-score" />
      </div>
      <p className="mt-2 text-xs text-[#94A3B8]">{health.note}</p>

      <div className="mt-4 space-y-2">
        {health.rooms.map((r) => (
          <div key={r.room_id} className="flex items-center justify-between rounded-xl bg-[#FAF7F2] border border-[#E2E8F0] px-4 py-3" data-testid={`health-room-${r.room_id}`}>
            <div className="flex items-center gap-2 min-w-0">
              <DoorOpen size={16} className="text-brand shrink-0" />
              <span className="font-medium text-navy truncate">{r.room_name}</span>
              <span className="text-xs text-[#94A3B8] shrink-0">· {r.photo_count} photos · {r.issue_count} issues</span>
            </div>
            <ScorePill score={r.score} />
          </div>
        ))}
      </div>

      {health.issues?.length > 0 && (
        <div className="mt-5">
          <p className="text-xs font-semibold uppercase tracking-wider text-[#94A3B8] mb-2">Itemized condition notes</p>
          <div className="space-y-2">
            {health.issues.map((it, i) => (
              <button
                key={it.photo_id}
                data-testid={`health-issue-${i}`}
                onClick={() => navigate(`/capture/${propertyId}/${it.room_id || ""}`)}
                className="w-full text-left rounded-xl border border-[#E2E8F0] p-3 hover:border-[#CBD5E1] transition-colors"
              >
                <div className="flex items-center gap-2">
                  <ConditionBadge condition={it.condition} />
                  <span className="text-sm font-medium text-navy">{it.room_name}</span>
                </div>
                <p className="mt-1 text-sm text-[#334155]">{it.summary}</p>
                {it.issues?.length > 0 && (
                  <p className="mt-1 text-xs text-[#92400E]">• {it.issues.join(" · ")}</p>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
}


function Celebration({ health, photoCount, createdAt, lockedAt, onShare, onView }) {
  const overall = health?.overall_score || "Documented";
  let timeStr = null;
  try {
    if (createdAt && lockedAt) {
      const mins = Math.max(1, Math.round((new Date(lockedAt) - new Date(createdAt)) / 60000));
      if (mins < 60) timeStr = `${mins} min`;
      else if (mins < 1440) timeStr = `${Math.round(mins / 60)} hr`;
    }
  } catch (e) { console.error("computing lock duration failed", e); }

  const colors = ["#0047FF", "#177245", "#F59E0B", "#002244", "#60A5FA", "#34D399"];
  const pieces = Array.from({ length: 40 }).map((_, i) => ({
    left: Math.random() * 100,
    delay: Math.random() * 0.6,
    dur: 2 + Math.random() * 1.5,
    color: colors[i % colors.length],
    rot: Math.random() * 360,
  }));

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-navy/70 backdrop-blur-sm px-4" data-testid="celebration-overlay">
      {pieces.map((p, i) => (
        <span key={i} className="dd-confetti" style={{ left: `${p.left}%`, background: p.color, animationDelay: `${p.delay}s`, animationDuration: `${p.dur}s`, transform: `rotate(${p.rot}deg)` }} />
      ))}
      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 12 }} animate={{ scale: 1, opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
        className="relative w-full max-w-md rounded-3xl bg-white p-8 text-center shadow-2xl"
      >
        <div className="dd-check-pop mx-auto grid place-items-center h-20 w-20 rounded-full bg-amber-soft text-amber">
          <CheckCircle2 size={44} strokeWidth={2.5} />
        </div>
        <h2 className="mt-5 font-heading text-3xl font-bold tracking-tight text-navy">Your home is protected ✓</h2>
        <p className="mt-2 text-[#475569]">Your move-in documentation is locked and timestamped.</p>

        <div className={`mt-6 grid ${timeStr ? "grid-cols-3" : "grid-cols-2"} gap-3`}>
          <CelebStat label="Health score" value={overall} />
          <CelebStat label="Photos" value={photoCount} />
          {timeStr && <CelebStat label="Time taken" value={timeStr} />}
        </div>

        <div className="mt-7 flex flex-col sm:flex-row gap-3">
          <Button data-testid="celebrate-share-btn" onClick={onShare} className="flex-1 h-12 rounded-xl bg-brand hover:bg-brand-hover text-white">
            <Share2 size={16} className="mr-2" /> Share with landlord
          </Button>
          <Button data-testid="celebrate-view-btn" variant="outline" onClick={onView} className="flex-1 h-12 rounded-xl border-[#E2E8F0]">
            <FileText size={16} className="mr-2" /> View report
          </Button>
        </div>
      </motion.div>
    </div>
  );
}

function CelebStat({ label, value }) {
  return (
    <div className="rounded-xl bg-[#FAF7F2] border border-[#E2E8F0] p-3">
      <p className="font-heading text-lg font-bold text-navy leading-tight truncate">{value}</p>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] mt-0.5">{label}</p>
    </div>
  );
}
